import React, { Component, ErrorInfo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary component to catch React errors
 * Prevents the entire app from crashing when a component throws an error
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error using logger utility
    // Logger will only log in development mode
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console

    }
    
    // In production, you might want to log to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } })
    
    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />
    }

    return this.props.children
  }
}

/**
 * Default error fallback component
 */
const ErrorFallback: React.FC<{ error: Error | null; onReset: () => void }> = ({ error, onReset }) => {
  const { t } = useTranslation()
  
  return (
    <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 shadow-sm max-w-md w-full">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2 font-['IBM_Plex_Sans_Arabic']">
            {t('error.something_went_wrong') || 'حدث خطأ ما'}
          </h2>
          <p className="text-gray-600 text-sm mb-4 font-['IBM_Plex_Sans_Arabic']">
            {t('error.please_refresh') || 'يرجى تحديث الصفحة أو المحاولة مرة أخرى'}
          </p>
          
          {import.meta.env.DEV && error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 mb-2">
                تفاصيل الخطأ (للتطوير فقط)
              </summary>
              <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                {error.toString()}
              </pre>
            </details>
          )}
          
          <button
            onClick={onReset}
            className="mt-4 px-6 py-2 bg-flutter-button text-white rounded-lg font-medium hover:bg-flutter-button/90 transition-colors font-['IBM_Plex_Sans_Arabic']"
          >
            {t('error.try_again') || 'حاول مرة أخرى'}
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="mt-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors font-['IBM_Plex_Sans_Arabic']"
          >
            {t('error.refresh_page') || 'تحديث الصفحة'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ErrorBoundary

